import { useEffect, useState, useContext } from 'react'

import { Group, Tooltip, Select, Text } from '@mantine/core'

import { useMutation } from '@redwoodjs/web'

import Button from 'src/components/Buttons/Button/Button'
import { toast } from 'src/components/Overlays/Toast/Toast'
import { RecruitersContext } from 'src/context/RecruitersContext'
import { BULK_RECRUITER_ASSIGNMENT } from 'src/graphql/applicants.graphql'
import IconUsers from 'src/icons/IconUsers'

const BulkRecruiterAssignmentDrawer = ({
  applicants,
  refetchQueries = [],
  onClose,
  setLoading,
  setResults,
}) => {
  const [selectedRecruiterId, setSelectedRecruiterId] = useState(null)

  const recruiters = useContext(RecruitersContext)

  // MUTATIONS:
  const [
    bulkRecruiterAssignment,
    { loading: bulkAssignmentLoading },
  ] = useMutation(BULK_RECRUITER_ASSIGNMENT, {
    refetchQueries: refetchQueries,
    onCompleted: (data) => {
      const results = data?.bulkRecruiterAssignment?.applicants
      if (setResults) {
        setResults(
          results?.length
            ? results.map((result) => ({
                applicantId: result.applicantId,
                success: result.success,
                errorMsg: result.errorMsg,
              }))
            : applicants.map((a) => ({
                applicantId: a.applicantId,
                success: true,
              }))
        )
      }
      toast('Applicants were reassigned successfully', 'success')
    },
    onError: () => {
      if (setResults) {
        setResults(
          applicants.map((a) => ({
            applicantId: a.applicantId,
            success: false,
            errorMsg: 'Failed to reassign recruiter',
          }))
        )
      }
      toast('Unable to reassign applicants, please try again.', 'error')
    },
  })

  useEffect(() => {
    if (setLoading) {
      setLoading(bulkAssignmentLoading)
    }
  }, [bulkAssignmentLoading, setLoading])

  const handleSubmit = () => {
    bulkRecruiterAssignment({
      variables: {
        applicants: applicants.map(
          (applicant) => applicant.applicantId || applicant
        ),
        recruiterId: selectedRecruiterId,
      },
    })
  }

  const isDisabled = () => {
    return applicants.length === 0 || !selectedRecruiterId
  }

  const selectedRecruiterName =
    recruiters.find((recruiter) => recruiter.value === selectedRecruiterId)
      ?.label || 'Unknown Recruiter'

  return (
    <>
      <div className="flex flex-col gap-4">
        <div className="flex flex-row items-center justify-between">
          <Text size="xs">Recruiter</Text>
          <div className="w-[250px]">
            <Select
              value={selectedRecruiterId}
              onChange={setSelectedRecruiterId}
              data={recruiters}
              required
              placeholder="Select a recruiter"
              searchable
              size="xs"
              classNames={{
                input: 'text-xs text-right',
                dropdown: 'border-doubleNickel-gray-200',
                option:
                  'text-xs text-doubleNickel-gray-900 data-[combobox-selected]:bg-transparent data-[combobox-active]:bg-transparent hover:bg-doubleNickel-gray-100',
              }}
            />
          </div>
        </div>
      </div>

      <Group className="mt-auto grid grid-cols-2">
        <Button
          text="Cancel"
          variant="outline"
          onClick={onClose}
          disabled={bulkAssignmentLoading}
        />
        <Tooltip
          disabled={!isDisabled()}
          label="Please select at least one applicant and a recruiter"
        >
          <Button
            text={
              selectedRecruiterId
                ? `Assign to ${selectedRecruiterName}`
                : 'Assign'
            }
            variant="filled"
            lefticon={<IconUsers />}
            disabled={isDisabled()}
            loading={bulkAssignmentLoading}
            onClick={() => {
              handleSubmit()
            }}
          />
        </Tooltip>
      </Group>
    </>
  )
}

export default BulkRecruiterAssignmentDrawer
